import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

/**
 * Query client configuration with optimized defaults for the Vera platform
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time - data considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Cache time - data kept in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry configuration
      retry: (failureCount, error: unknown) => {
        // Don't retry on authentication errors (401, 403)
        if ((error as { status?: number })?.status === 401 || (error as { status?: number })?.status === 403) {
          return false;
        }
        // Don't retry on client errors (4xx except 401, 403)
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) {
          return false;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      // Retry delay with exponential backoff
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      /*
       * `true`, NOT `'always'` — and the difference is the whole point.
       *
       * `'always'` refetches regardless of staleness, which OVERRIDES `staleTime`
       * and made the 5 minutes above dead config: every remount paid a network
       * round-trip before the page could show anything. Both of these lines
       * previously said `'always'` under comments describing the opposite
       * behaviour ("only if data is stale", "don't refetch if data is fresh"),
       * so the stated intent and the value had been contradicting each other.
       * `true` is what those comments describe: serve cache immediately, refetch
       * only when stale.
       *
       * Measured on returning to the certificate page from Share: the page
       * mounted WHOLE but ~600ms late, with no skeleton and no animation — it
       * simply was not there, then it was. That gap was this refetch, and it read
       * as content flashing in after the slide instead of the slide revealing a
       * page that was already ready.
       *
       * Staleness is still bounded by `staleTime` (5 min) and any mutation that
       * changes a record already invalidates its query explicitly, so this trades
       * no correctness for the instant paint.
       */
      refetchOnWindowFocus: true,
      // Reconnect stays 'always': coming back from offline, cached data may be
      // arbitrarily out of date and "fresh" is not a claim we can make about it.
      refetchOnReconnect: 'always',
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations only once
      retry: 1,
      // Retry delay for mutations
      retryDelay: 2000,
      // Global error handling for mutations
      onError: (error: unknown) => {
        console.error('Mutation error:', error);
        
        // Handle specific error types
        const status = (error as { status?: number })?.status;
        if (status === 401) {
          // Redirect to login or refresh token
          console.warn('Authentication error - user may need to re-login');
        } else if (status === 403) {
          console.warn('Authorization error - user lacks permissions');
        } else if (status && status >= 500) {
          console.error('Server error - please try again later');
        }
      },
      // Global success handling for mutations
      onSuccess: () => {
        // You can add global success handling here if needed
        // For example, invalidating related queries
      },
    },
  },
});

/**
 * Props for QueryProvider
 */
interface QueryProviderProps {
  children: React.ReactNode;
}

/**
 * React Query provider component that wraps the application
 * Provides query client context and devtools for development
 */
export const QueryProvider: React.FC<QueryProviderProps> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
};

/**
 * Hook to access the query client instance
 * Useful for manual cache management and imperative operations
 */
export const useQueryClient = () => {
  return queryClient;
};

/**
 * Utility function to invalidate all queries
 * Useful for global state refresh scenarios
 */
export const invalidateAllQueries = async () => {
  await queryClient.invalidateQueries();
};

/**
 * Utility function to clear all cached data
 * Useful for logout scenarios
 */
export const clearQueryCache = () => {
  queryClient.clear();
};

/**
 * Utility function to set default options at runtime
 * Can be used to update configuration based on user preferences
 */
export const updateQueryDefaults = (options: Parameters<typeof queryClient.setDefaultOptions>[0]) => {
  queryClient.setDefaultOptions(options);
};

export default QueryProvider;
